import React, { useState, useEffect } from 'react';
import {
  Box,
  Breadcrumbs,
  Link,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Button,
  CircularProgress,
  LinearProgress,
  Alert,
  Paper,
  Divider
} from '@mui/material';
import {
  ExpandMore,
  AccountTree,
  Business,
  Assignment,
  Add as AddIcon,
  ChevronRight
} from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import OrganizationView from '../components/OrganizationView';
import CompanyView from '../components/CompanyView';
import AccountView from '../components/AccountView';
import TaskView from '../components/TaskView';
import type { BulkBudgetProgressItem } from '../types/accountBudget';

interface NavigationContext {
  type: 'tree' | 'organization' | 'company' | 'account' | 'task';
  id?: string;
  name?: string;
  parentContext?: NavigationContext;
}

interface Organization {
  id: string;
  name: string;
  slug: string;
  description?: string;
  isActive: boolean;
}

interface Company {
  id: string;
  name: string;
  companyNumber?: string | null;
  organizationId: string;
  description?: string;
  isActive: boolean;
}

interface Account {
  id: string;
  name: string;
  companyId: string;
  organizationId: string;
  description?: string;
  isActive: boolean;
}

interface Task {
  id: string;
  name: string;
  accountId: string;
  organizationId: string;
  taskNumber?: string;
  isActive: boolean;
}

export default function Manage() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentContext, setCurrentContext] = useState<NavigationContext>({ type: 'tree' });
  const [navigationHistory, setNavigationHistory] = useState<NavigationContext[]>([]);
  
  // Data state
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [organizationRoles, setOrganizationRoles] = useState<{ [orgId: string]: string }>({});
  const [companies, setCompanies] = useState<{ [orgId: string]: Company[] }>({});
  const [accounts, setAccounts] = useState<{ [companyId: string]: Account[] }>({});
  const [tasks, setTasks] = useState<{ [accountId: string]: Task[] }>({});
  const [expandedOrgs, setExpandedOrgs] = useState<Set<string>>(new Set());
  const [userRole, setUserRole] = useState<string>('MEMBER');
  const [refreshKey, setRefreshKey] = useState(0);
  const [budgetProgressByOrg, setBudgetProgressByOrg] = useState<Record<string, Record<string, BulkBudgetProgressItem>>>({});
  
  // Load all user organizations and their roles
  useEffect(() => {
    loadUserOrganizations();
  }, []);

  // Load current organization's hierarchy when selected. Refresh org list when
  // current org is not in the list (e.g. after creating a new org from the selector).
  useEffect(() => {
    if (!organizationId) return;
    const inList = organizations.some((o) => o.id === organizationId);
    if (!inList) {
      loadUserOrganizations();
    } else {
      loadCurrentOrganization();
      loadUserRole();
    }
  }, [organizationId, organizations]);

  // Refresh companies when returning from AddCompany page
  useEffect(() => {
    const state = routeLocation.state as any;
    if (state?.context?.type === 'organization' && state?.context?.id) {
      const orgId = state.context.id;
      // Clear cached companies to force fresh reload
      setCompanies(prev => {
        const updated = { ...prev };
        delete updated[orgId];
        return updated;
      });
      // Reload companies if organization is expanded (will show immediately)
      // If not expanded, they'll load when user expands the accordion
      if (expandedOrgs.has(orgId)) {
        // Force reload and trigger re-render
        loadCompanies(orgId, true).then(() => {
          // Increment refresh key to force re-render of the tree view
          setRefreshKey(prev => prev + 1);
        });
      }
      // Clear location state to prevent repeated refreshes
      window.history.replaceState({}, document.title);
    } else if (state?.context?.type === 'company' && state?.context?.id) {
      // Refresh accounts when returning from AddAccount page
      const companyId = state.context.id;
      // Clear cached accounts for this company
      setAccounts(prev => {
        const updated = { ...prev };
        delete updated[companyId];
        return updated;
      });
      // Reload accounts for this company
      loadAccounts(companyId);
      // Clear location state to prevent repeated refreshes
      window.history.replaceState({}, document.title);
    }
  }, [routeLocation.state]);

  // Load companies for expanded organizations when they become expanded
  useEffect(() => {
    expandedOrgs.forEach((orgId) => {
      if (!companies[orgId]) {
        // Load companies if organization is expanded but companies aren't loaded yet
        loadCompanies(orgId);
      }
    });
  }, [expandedOrgs]);

  // Lazy load accounts when companies are loaded (only for expanded organizations)
  useEffect(() => {
    expandedOrgs.forEach((orgId) => {
      if (companies[orgId]) {
        companies[orgId].forEach((company) => {
          if (!accounts[company.id]) {
            loadAccounts(company.id);
          }
        });
      }
    });
  }, [companies, expandedOrgs]);

  // Tasks are no longer shown in tree view - only in detail views (AccountView)

  const loadUserOrganizations = async () => {
    try {
      const response = await api.getUserOrganizations();
      if (response.success && response.data) {
        // Transform to get organizations with roles
        const orgs = response.data.map((uo: any) => uo.organization);
        const roles: { [orgId: string]: string } = {};
        response.data.forEach((uo: any) => {
          roles[uo.organization.id] = (uo.role || 'MEMBER').toUpperCase();
        });
        
        // Sort organizations alphabetically
        orgs.sort((a: Organization, b: Organization) => a.name.localeCompare(b.name));
        
        setOrganizations(orgs);
        setOrganizationRoles(roles);
        
        // Set all organizations as expanded by default
        // Use setTimeout to ensure state update happens after render
        setTimeout(() => {
          setExpandedOrgs(new Set(orgs.map((o: Organization) => o.id)));
        }, 0);
        
        // Load companies for all organizations (lazy load on expand)
        // For now, we'll load when expanded
      }
    } catch (err: any) {
      console.error('Error loading organizations:', err);
      setError(err.response?.data?.message || 'Failed to load organizations');
    }
  };

  const loadUserRole = async () => {
    try {
      if (organizationId && organizationRoles[organizationId]) {
        setUserRole(organizationRoles[organizationId]);
      }
    } catch (err: any) {
      console.error('Error loading user role:', err);
    }
  };

  const loadCurrentOrganization = async () => {
    try {
      setLoading(true);
      setError(null);
      // Verify organization exists in our loaded list
      const org = organizations.find(o => o.id === organizationId);
      if (!org && organizations.length === 0) {
        // Reload organizations if not found and list is empty
        await loadUserOrganizations();
      }
    } catch (err: any) {
      console.error('Error loading organization:', err);
      setError(err.response?.data?.message || 'Failed to load organization');
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = async (orgId: string, forceRefresh: boolean = false): Promise<void> => {
    if (companies[orgId] && !forceRefresh) return Promise.resolve(); // Already loaded
    
    try {
      const response = await api.getCompanies({ organizationId: orgId, isActive: 'true' });
      const companyList = response.data || [];
      // Sort alphabetically
      companyList.sort((a: Company, b: Company) => a.name.localeCompare(b.name));
      setCompanies(prev => ({ ...prev, [orgId]: companyList }));
      // Accounts will be loaded via useEffect when companies state updates
    } catch (err: any) {
      console.error('Error loading companies:', err);
    }
  };

  const loadBudgetProgress = async (orgId: string) => {
    try {
      const response = await api.getAccountsBudgetProgress(orgId);
      if (response.success && Array.isArray(response.data)) {
        const map: Record<string, BulkBudgetProgressItem> = {};
        for (const item of response.data as BulkBudgetProgressItem[]) {
          map[item.accountId] = item;
        }
        setBudgetProgressByOrg((prev) => ({ ...prev, [orgId]: map }));
      }
    } catch (err) {
      console.error('Error loading budget progress:', err);
    }
  };

  const loadAccounts = async (companyId: string): Promise<void> => {
    if (accounts[companyId]) return Promise.resolve(); // Already loaded
    
    try {
      const response = await api.getAccounts({ companyId, isActive: 'true' });
      const accountList = response.data || [];
      // Sort alphabetically
      accountList.sort((a: Account, b: Account) => a.name.localeCompare(b.name));
      setAccounts(prev => ({ ...prev, [companyId]: accountList }));
      // Tasks will be loaded via useEffect when accounts state updates
    } catch (err: any) {
      console.error('Error loading accounts:', err);
    }
  };

  const loadTasks = async (accountId: string) => {
    if (tasks[accountId]) return; // Already loaded
    
    try {
      const response = await api.getTasks({ accountId, isActive: 'true' });
      const taskList = response.data || [];
      // Sort alphabetically
      taskList.sort((a: Task, b: Task) => a.name.localeCompare(b.name));
      setTasks(prev => ({ ...prev, [accountId]: taskList }));
    } catch (err: any) {
      console.error('Error loading tasks:', err);
    }
  };

  const handleOrgExpand = (orgId: string, isExpanding: boolean) => {
    const newExpanded = new Set(expandedOrgs);
    if (isExpanding) {
      newExpanded.add(orgId);
      // Lazy load companies when organization expands
      loadCompanies(orgId);
      loadBudgetProgress(orgId);
    } else {
      newExpanded.delete(orgId);
    }
    setExpandedOrgs(newExpanded);
  };

  const budgetProgressPercent = (item: BulkBudgetProgressItem): number => {
    if (item.target <= 0) return item.registered > 0 ? 100 : 0;
    return Math.min(100, (item.registered / item.target) * 100);
  };

  const handleCompanyClick = (companyId: string, companyName: string, orgId: string, orgName: string) => {
    // Load accounts for all companies in this organization when company is clicked
    // This is lazy loading - we load accounts when user interacts with company
    if (!accounts[companyId]) {
      loadAccounts(companyId);
    }
    // Navigate to company view
    handleNavigate({
      type: 'company',
      id: companyId,
      name: companyName,
      parentContext: { type: 'organization', id: orgId, name: orgName }
    });
  };

  const handleAccountClick = (accountId: string, accountName: string, companyId: string, companyName: string, orgId: string, orgName: string) => {
    // Navigate to account view (tasks will be loaded in AccountView if needed)
    handleNavigate({
      type: 'account',
      id: accountId,
      name: accountName,
      parentContext: {
        type: 'company',
        id: companyId,
        name: companyName,
        parentContext: { type: 'organization', id: orgId, name: orgName }
      }
    });
  };

  const handleTaskClick = (taskId: string, taskName: string, accountId: string, accountName: string, companyId: string, companyName: string, orgId: string, orgName: string) => {
    // Navigate to task view
    handleNavigate({
      type: 'task',
      id: taskId,
      name: taskName,
      parentContext: {
        type: 'account',
        id: accountId,
        name: accountName,
        parentContext: {
          type: 'company',
          id: companyId,
          name: companyName,
          parentContext: { type: 'organization', id: orgId, name: orgName }
        }
      }
    });
  };

  const formatRole = (role: string): string => {
    if (!role) return 'Member';
    const normalized = role.toUpperCase();
    if (normalized === 'OWNER') return 'Owner';
    if (normalized === 'MANAGER') return 'Manager';
    return 'Member';
  };

  const handleNavigate = (context: NavigationContext) => {
    setNavigationHistory(prev => [...prev, currentContext]);
    setCurrentContext(context);
  };

  const handleBreadcrumbClick = (context: NavigationContext) => {
    setCurrentContext(context);
    // Remove contexts after this one from history
    const index = navigationHistory.findIndex(c => c === context);
    if (index >= 0) {
      setNavigationHistory(navigationHistory.slice(0, index));
    } else {
      setNavigationHistory([]);
    }
  };

  const buildBreadcrumbs = (): NavigationContext[] => {
    const crumbs: NavigationContext[] = [{ type: 'tree' }];
    
    // Build full hierarchy path from parentContext chain
    if (currentContext.type !== 'tree') {
      const path: NavigationContext[] = [];
      let context: NavigationContext | undefined = currentContext;
      
      // Build path backwards from current context
      while (context) {
        path.unshift(context);
        context = context.parentContext;
      }
      
      crumbs.push(...path);
    }
    
    return crumbs;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const breadcrumbs = buildBreadcrumbs();

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Breadcrumb Navigation */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Breadcrumbs separator={<ChevronRight fontSize="small" />}>
          {breadcrumbs.map((crumb, index) => (
            <Link
              key={index}
              component="button"
              variant="body1"
              onClick={() => {
                if (index < breadcrumbs.length - 1) {
                  handleBreadcrumbClick(crumb);
                }
              }}
              sx={{
                cursor: index < breadcrumbs.length - 1 ? 'pointer' : 'default',
                textDecoration: index < breadcrumbs.length - 1 ? 'underline' : 'none',
                color: index < breadcrumbs.length - 1 ? 'primary.main' : 'text.primary'
              }}
            >
              {crumb.type === 'tree' ? 'Manage' : crumb.name}
            </Link>
          ))}
        </Breadcrumbs>
      </Paper>

      {/* Content Area */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {currentContext.type === 'tree' ? (
          // Tree View - One accordion per organization with flat tree structure inside
          <Box>
            {organizations.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                  No organizations available
                </Typography>
              </Box>
            ) : (
              organizations.map((org) => {
                const orgRole = organizationRoles[org.id] || 'MEMBER';
                const canAddCompany = orgRole === 'OWNER' || orgRole === 'MANAGER';
                
                return (
                  <Accordion
                    key={org.id}
                    expanded={expandedOrgs.has(org.id)}
                    onChange={(_, isExpanded) => handleOrgExpand(org.id, isExpanded)}
                    sx={{ 
                      mb: 2,
                      '&:before': {
                        display: 'none' // Remove default accordion divider
                      },
                      '&.Mui-expanded': {
                        margin: '0 0 16px 0'
                      }
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      sx={{ 
                        backgroundColor: 'transparent',
                        borderLeft: expandedOrgs.has(org.id) ? '3px solid' : '3px solid transparent',
                        borderColor: 'primary.main',
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        },
                        '& .MuiAccordionSummary-content': {
                          alignItems: 'center',
                          gap: 2
                        },
                        '&.Mui-expanded': {
                          backgroundColor: 'transparent',
                          borderLeft: '3px solid',
                          borderColor: 'primary.main'
                        }
                      }}
                    >
                      {/* 4-column grid layout */}
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                        {/* Column 1: Organization Name (clickable - only text area) */}
                        <Typography
                          variant="h6"
                          component="span"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNavigate({ type: 'organization', id: org.id, name: org.name });
                          }}
                          sx={{
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: '16px',
                            display: 'inline-block', // Only take space needed for text
                            '&:hover': {
                              color: 'primary.main',
                              textDecoration: 'underline'
                            }
                          }}
                        >
                          {org.name}
                        </Typography>
                        
                        {/* Spacer to push content to the right */}
                        <Box sx={{ flexGrow: 1 }} />
                        
                        {/* Column 2: + Company Button (conditional) */}
                        {canAddCompany && (
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/org/${org.id}/manage/add-company`);
                            }}
                            sx={{
                              minWidth: 'auto',
                              px: 1.5,
                              py: 0.5,
                              fontSize: '12px',
                              textTransform: 'none'
                            }}
                          >
                            + Company
                          </Button>
                        )}
                        
                        {/* Column 3: Role Text */}
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            fontSize: '11px',
                            minWidth: '60px',
                            textAlign: 'right'
                          }}
                        >
                          {formatRole(orgRole)}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{
                        backgroundColor: 'transparent',
                        pt: 2,
                        pb: 2
                      }}
                    >
                      {/* Flat tree view: Companies, Accounts, Tasks */}
                      {expandedOrgs.has(org.id) && (
                        <Box key={`org-${org.id}-${refreshKey}`} sx={{ pl: 2 }}>
                          {companies[org.id] && companies[org.id].length > 0 ? (
                            companies[org.id].map((company) => (
                              <Box key={company.id} sx={{ mb: 1 }}>
                                {/* Company Item (16px indent) */}
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    pl: 2,
                                    py: 0.75,
                                    px: 1,
                                    cursor: 'pointer',
                                    borderLeft: '3px solid',
                                    borderColor: 'info.main',
                                    borderRadius: 0,
                                    mb: 0.5,
                                    '&:hover': {
                                      backgroundColor: 'action.hover'
                                    }
                                  }}
                                  onClick={() => handleCompanyClick(company.id, company.name, org.id, org.name)}
                                >
                                  <AccountTree sx={{ fontSize: 20 }} />
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'baseline',
                                      gap: 1,
                                      flexWrap: 'wrap',
                                      flexGrow: 1,
                                      minWidth: 0
                                    }}
                                  >
                                    <Typography
                                      variant="body1"
                                      sx={{ fontWeight: 600, fontSize: '16px' }}
                                    >
                                      {company.name}
                                    </Typography>
                                    {company.companyNumber ? (
                                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                                        ({company.companyNumber})
                                      </Typography>
                                    ) : null}
                                  </Box>
                                </Box>
                                
                                {/* Accounts for this company (32px indent) */}
                                {accounts[company.id] && accounts[company.id].length > 0 && (
                                  <Box sx={{ pl: 4 }}>
                                    {accounts[company.id].map((account) => {
                                      const progress = budgetProgressByOrg[org.id]?.[account.id];
                                      return (
                                      <Box key={account.id} sx={{ mb: 0.5 }}>
                                        {/* Account Item */}
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 0.25,
                                            py: 0.5,
                                            px: 1,
                                            cursor: 'pointer',
                                            borderLeft: '3px solid',
                                            borderColor: 'success.main',
                                            borderRadius: 0,
                                            mb: 0.5,
                                            '&:hover': {
                                              backgroundColor: 'action.hover'
                                            }
                                          }}
                                          onClick={() => handleAccountClick(account.id, account.name, company.id, company.name, org.id, org.name)}
                                        >
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Typography
                                            component="span"
                                            sx={{
                                              fontSize: '12px',
                                              color: 'text.secondary',
                                              mr: 0.5
                                            }}
                                          >
                                            └
                                          </Typography>
                                          <Assignment sx={{ fontSize: 18 }} />
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontWeight: 600,
                                              fontSize: '15px',
                                              flexGrow: 1
                                            }}
                                          >
                                            {account.name}
                                          </Typography>
                                          </Box>
                                          {progress && (
                                            <LinearProgress
                                              variant="determinate"
                                              value={budgetProgressPercent(progress)}
                                              color={progress.percent > 100 ? 'error' : 'primary'}
                                              sx={{ ml: 3, height: 4, borderRadius: 1 }}
                                            />
                                          )}
                                        </Box>
                                      </Box>
                                    );})}
                                  </Box>
                                )}
                              </Box>
                            ))
                          ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>
                              No companies. {canAddCompany && "Click '+ Company' to add one."}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })
            )}
          </Box>
        ) : currentContext.type === 'organization' && currentContext.id ? (
          // Organization View
          <OrganizationView
            organizationId={currentContext.id}
            organizationName={currentContext.name || ''}
            onBack={() => setCurrentContext({ type: 'tree' })}
            onOpenCompany={(companyId: string, companyName: string) => {
              // Find the company in our loaded data to build context
              const company = Object.values(companies).flat().find(c => c.id === companyId);
              if (company && currentContext.id) {
                handleNavigate({
                  type: 'company',
                  id: companyId,
                  name: companyName,
                  parentContext: { type: 'organization', id: currentContext.id, name: currentContext.name || '' }
                });
              }
            }}
            userRole={organizationRoles[currentContext.id] || 'MEMBER'}
          />
        ) : currentContext.type === 'company' && currentContext.id && currentContext.parentContext ? (
          // Company View
          <CompanyView
            companyId={currentContext.id}
            companyName={currentContext.name || ''}
            organizationId={currentContext.parentContext.id || organizationId || ''}
            onBack={() => {
              if (currentContext.parentContext?.type === 'organization') {
                handleNavigate(currentContext.parentContext);
              } else {
                setCurrentContext({ type: 'tree' });
              }
            }}
            onOpenAccount={(accountId: string, accountName: string) => {
              // Find the account in our loaded data to build context
              const account = Object.values(accounts).flat().find(a => a.id === accountId);
              if (account && currentContext.id) {
                handleNavigate({
                  type: 'account',
                  id: accountId,
                  name: accountName,
                  parentContext: {
                    type: 'company',
                    id: currentContext.id,
                    name: currentContext.name || '',
                    parentContext: currentContext.parentContext
                  }
                });
              }
            }}
            userRole={currentContext.parentContext?.id ? (organizationRoles[currentContext.parentContext.id] || 'MEMBER') : userRole}
          />
        ) : currentContext.type === 'account' && currentContext.id && currentContext.parentContext ? (
          // Account View
          <AccountView
            accountId={currentContext.id}
            accountName={currentContext.name || ''}
            companyId={currentContext.parentContext.id || ''}
            organizationId={currentContext.parentContext.parentContext?.id || organizationId || ''}
            onBack={() => {
              if (currentContext.parentContext) {
                handleNavigate(currentContext.parentContext);
              } else {
                setCurrentContext({ type: 'tree' });
              }
            }}
            onOpenTask={(taskId: string, taskName: string) => {
              if (!currentContext.id) return;
              handleNavigate({
                type: 'task',
                id: taskId,
                name: taskName,
                parentContext: {
                  type: 'account',
                  id: currentContext.id,
                  name: currentContext.name || '',
                  parentContext: currentContext.parentContext
                }
              });
            }}
            userRole={currentContext.parentContext?.parentContext?.id ? (organizationRoles[currentContext.parentContext.parentContext.id] || 'MEMBER') : userRole}
          />
        ) : currentContext.type === 'task' && currentContext.id && currentContext.parentContext ? (
          // Task View
          <TaskView
            taskId={currentContext.id}
            taskName={currentContext.name || ''}
            accountId={currentContext.parentContext.id || ''}
            organizationId={
              currentContext.parentContext.parentContext?.parentContext?.id || 
              currentContext.parentContext.parentContext?.id || 
              organizationId || 
              ''
            }
            onBack={() => {
              if (currentContext.parentContext) {
                handleNavigate(currentContext.parentContext);
              } else {
                setCurrentContext({ type: 'tree' });
              }
            }}
            userRole={
              currentContext.parentContext?.parentContext?.parentContext?.id 
                ? (organizationRoles[currentContext.parentContext.parentContext.parentContext.id] || 'MEMBER')
                : currentContext.parentContext?.parentContext?.id
                  ? (organizationRoles[currentContext.parentContext.parentContext.id] || 'MEMBER')
                  : userRole
            }
          />
        ) : (
          // Fallback placeholder
          <Box sx={{ p: 2 }}>
            <Typography variant="h5" gutterBottom>
              {currentContext.name || 'Unknown'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View not available
            </Typography>
            <Button
              variant="outlined"
              onClick={() => setCurrentContext({ type: 'tree' })}
              sx={{ mt: 2 }}
            >
              Back to Tree View
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
